import { Policy } from '../customer/Policy';
import { ApiUtils } from '../gql/ApiUtils';
import { FetchResult } from 'apollo-fetch';
import { Utils } from '@hit/utils';
import { CurrentStageResponse, ExamStatus, UwRequestBuilder } from '@hit/underwriting-api-client';
import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../logger/UILoggerSingleton';

@supertypeClass({ toClient: ['admin'], toServer: false })
export class AdminUtil {
  static readonly OR1Stage = 'OR1';
  static readonly FilterOutStage = ['UD1', 'RD1', 'OR1', 'UW2', 'HL1'];
  static readonly sqlGetStage = `select substring(temp.stage, 2, position(' ' in temp.stage)-2) as "stageDef"
        from (
            select (s."stageDef"::json->'name')::varchar as stage
            from policy p join application a on p.application_id = a."_id"
              join workflow w on w."referenceId" = a."_id" and w."referenceType" = 'ApplicationId' and w."name" = 'Hit'
              join stage s on s.workflow_id = w."_id"
            where p."policyNumber" = :policyNumber
            order by s."startTime" desc
        ) temp`;

  static async getWorkflowStage(policyNumber: string): Promise<{ rows: { stageDef: string }[] }> {
    return Policy.getKnex().client.raw(AdminUtil.sqlGetStage, { policyNumber: policyNumber });
  }

  static async getWorkflowPrevStage(policyNumber: string, currStage: string = 'OR1'): Promise<string> {
    const result: { rows: { stageDef: string }[] } = await AdminUtil.getWorkflowStage(policyNumber);

    if (result && result.rows[0] && result.rows[0].stageDef === currStage) {
      const found = result.rows.find((row) => !AdminUtil.FilterOutStage.includes(row.stageDef));
      return found ? found.stageDef : null;
    }
    return null;
  }

  static async workflowContainsStage(policyNumber: string, targetStages: string[]): Promise<boolean> {
    const result: { rows: { stageDef: string }[] } = await AdminUtil.getWorkflowStage(policyNumber);
    if (result && result.rows) {
      const found = result.rows.find((row) => targetStages.includes(row.stageDef));
      if (found) {
        return true;
      }
    }
    return false;
  }

  static async getExamStatus(policyList: string[], latestStatusOnly: boolean = false): Promise<ExamStatus[]> {
    if (policyList.length === 0) {
      throw new Error('No policy provided.');
    }
    const apiEndpointName = UwRequestBuilder.getClientEndpoint.examStatus; //getExamStatus
    const request = UwRequestBuilder.setRequest.examStatus(policyList, latestStatusOnly);
    const query = UwRequestBuilder.getDefaultQueryArg.examStatus;
    const metadata = {
      time: Utils.now(),
    };

    try {
      const interaction = await ApiUtils.interactWithHitApiOutOfActivity(apiEndpointName, request, query, metadata);
      const fetchResult = interaction.interactionResponse as FetchResult;

      if (fetchResult.data && fetchResult.data.examStatus) {
        return fetchResult.data.examStatus.result;
      }

      UILoggerSingleton.getInstance().error(
        {
          module: 'AdminUtil',
          function: 'getExamStatus',
          category: UICategory.request,
          error: {
            message: fetchResult.errors,
            isHumanRelated: false,
          },
        },
        'Fetch exam status error'
      );
    } catch (error) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'AdminUtil',
          function: 'getExamStatus',
          category: UICategory.request,
          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
          },
        },
        'Failed to fetch from underwriting'
      );
    }
    return [];
  }

  static async getCurrentUWWorkflowStage(policyNumber: string): Promise<CurrentStageResponse> {
    const apiEndpoint = UwRequestBuilder.getClientEndpoint.currentStage;
    const request = UwRequestBuilder.setRequest.currentStage(policyNumber);
    const query = UwRequestBuilder.getDefaultQueryArg.currentStage;

    const metadata = {
      time: Utils.now(),
    };

    try {
      const interaction = await ApiUtils.interactWithHitApiOutOfActivity(apiEndpoint, request, query, metadata);
      const fetchResult = interaction.interactionResponse as FetchResult;

      if (fetchResult && fetchResult.data) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'AdminUtil',
            function: 'getCurrentUWWorkflow',
            category: UICategory.request,
            context: { piiLevel: UIPiiLevelEnum.s2 },
            data: fetchResult.data,
          },
          'Fetch to underwriting for workflow details successful'
        );
        return fetchResult.data.currentStage;
      } else {
        UILoggerSingleton.getInstance().info(
          {
            module: 'AdminUtil',
            function: 'getCurrentUWWorkflow',
            category: UICategory.request,
            context: { piiLevel: UIPiiLevelEnum.s2 },
            data: fetchResult.data,
          },
          'Fail to fetch to underwriting for workflow details'
        );
        return {
          currentStage: '',
          currentState: '',
          currentSubstate: '',
        } as CurrentStageResponse;
      }
    } catch (error) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'AdminUtil',
          function: 'getCurrentUWWorkflow',
          category: UICategory.request,
          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
          },
        },
        'Failed to fetch workflow details from underwriting'
      );
    }

    // set the stage details to empty when error
    return {
      currentStage: '',
      currentState: '',
      currentSubstate: '',
    } as CurrentStageResponse;
  }
}
